import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { CHART_COLORS } from "../../_components/chartColors";
import type { Peer } from "../../_components/scoring-types";
import { DriverDetail } from "./DriverDetail";
import type { KpiDriver } from "./scoring-category-types";

interface DriverAccordionListProps {
  drivers: KpiDriver[];
  peerColumns: (Peer | null)[];
  accountName: string;
  expandAll?: boolean;
}

/**
 * Score Badge Component (uses firm colors, not green)
 */
function ScoreBadge({
  score,
  colorIdx,
}: {
  score: number | null;
  colorIdx: number;
}) {
  if (score === null) {
    return (
      <span className="text-xs text-muted-foreground tabular-nums">&mdash;</span>
    );
  }

  const color = CHART_COLORS[colorIdx % CHART_COLORS.length];
  return (
    <div
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold tabular-nums"
      style={{
        background: `color-mix(in oklch, ${color}, transparent 82%)`,
        color,
      }}
    >
      {score.toFixed(1)}
    </div>
  );
}

/**
 * Single driver card — same Card+Collapsible pattern as KpiDriverCard
 */
function DriverRow({
  driver,
  peerColumns,
  accountName,
  expandAll = false,
}: {
  driver: KpiDriver;
  peerColumns: (Peer | null)[];
  accountName: string;
  expandAll?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(expandAll);

  useEffect(() => {
    setIsOpen(expandAll);
  }, [expandAll]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="gap-0 py-0">
        <CollapsibleTrigger className="w-full">
          <CardHeader className="p-0">
            <div className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors cursor-pointer">
              {/* Title — breaks onto multiple lines if long */}
              <div className="min-w-0 flex-1 pr-4 text-left text-sm font-medium text-foreground break-words">
                {driver.title}
              </div>

              {/* Scores + chevron */}
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-1.5">
                  <ScoreBadge score={driver.account_score} colorIdx={0} />
                  {driver.peer_scores.map((score, scoreIdx) => (
                    <ScoreBadge
                      key={peerColumns[scoreIdx]?.id ?? scoreIdx}
                      score={score}
                      colorIdx={scoreIdx + 1}
                    />
                  ))}
                </div>
                <ChevronDown
                  className={cn(
                    "h-5 w-5 text-muted-foreground transition-transform shrink-0",
                    isOpen && "rotate-180",
                  )}
                />
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="border-t border-border px-4 py-4">
            <DriverDetail
              driver={driver}
              accountName={accountName}
              peerColumns={peerColumns}
            />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

/**
 * Main Driver Accordion List
 */
export function DriverAccordionList({
  drivers,
  peerColumns,
  accountName,
  expandAll = false,
}: DriverAccordionListProps) {
  if (drivers.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No drivers available</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {drivers.map((driver) => (
        <DriverRow
          key={driver.title}
          driver={driver}
          peerColumns={peerColumns}
          accountName={accountName}
          expandAll={expandAll}
        />
      ))}
    </div>
  );
}
